<script lang="ts">
import type { DateValue } from 'reka-ui'

export interface CalendarMonthGridProps {
  placeholder: DateValue
  minValue?: DateValue
  maxValue?: DateValue
}

export interface CalendarMonthGridEmits {
  'update:placeholder': [value: DateValue]
  select: [value: DateValue]
}
</script>

<script setup lang="ts">
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
import { getLocalTimeZone, today } from '@internationalized/date'
import { computed } from 'vue'

const props = defineProps<CalendarMonthGridProps>()
const emits = defineEmits<CalendarMonthGridEmits>()

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const now = today(getLocalTimeZone())

const isPrevYearDisabled = computed(() => (props.minValue ? props.placeholder.year <= props.minValue.year : false))
const isNextYearDisabled = computed(() => (props.maxValue ? props.placeholder.year >= props.maxValue.year : false))

function isMonthDisabled(monthIndex: number): boolean {
  const month = monthIndex + 1
  const year = props.placeholder.year
  if (props.maxValue && (year > props.maxValue.year || (year === props.maxValue.year && month > props.maxValue.month)))
    return true
  if (props.minValue && (year < props.minValue.year || (year === props.minValue.year && month < props.minValue.month)))
    return true
  return false
}

function isMonthSelected(monthIndex: number): boolean {
  return props.placeholder.month === monthIndex + 1
}

function isCurrentMonth(monthIndex: number): boolean {
  return now.year === props.placeholder.year && now.month === monthIndex + 1
}

function selectMonth(monthIndex: number): void {
  if (isMonthDisabled(monthIndex)) return
  emits('select', props.placeholder.set({ month: monthIndex + 1, day: 1 }))
}
</script>

<template>
  <div class="w-[252px]">
    <div class="mb-3 flex items-center justify-between">
      <button
        type="button"
        :disabled="isPrevYearDisabled"
        aria-label="Previous year"
        class="inline-flex h-7 w-7 items-center justify-center rounded-md text-accent transition-colors hover:bg-neutral-300 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
        @click="emits('update:placeholder', props.placeholder.subtract({ years: 1 }))"
      >
        <ChevronLeftIcon class="h-4 w-4" />
      </button>
      <span class="text-sm font-semibold text-accent">{{ props.placeholder.year }}</span>
      <button
        type="button"
        :disabled="isNextYearDisabled"
        aria-label="Next year"
        class="inline-flex h-7 w-7 items-center justify-center rounded-md text-accent transition-colors hover:bg-neutral-300 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
        @click="emits('update:placeholder', props.placeholder.add({ years: 1 }))"
      >
        <ChevronRightIcon class="h-4 w-4" />
      </button>
    </div>

    <div class="grid grid-cols-3 gap-2">
      <button
        v-for="(label, index) in MONTH_LABELS"
        :key="label"
        type="button"
        :disabled="isMonthDisabled(index)"
        class="h-9 rounded-md text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:text-disabled/30"
        :class="
          isMonthSelected(index)
            ? 'bg-accent text-white'
            : isCurrentMonth(index)
              ? 'text-accent hover:bg-neutral-300'
              : 'text-primal hover:bg-neutral-300'
        "
        @click="selectMonth(index)"
      >
        {{ label }}
      </button>
    </div>
  </div>
</template>
